import react, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, getDoc, doc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage'; 
import { loadStripe } from '@stripe/stripe-js';
import { Link, useNavigate } from 'react-router-dom';
import '../../Styles/Auctions/createAuctionItemStyle.css';

const auctionDb = "AuctionItems"
// const auctionDb = "TestAuctionItems"

function CreateAuctionItem() {
    const [name, setName] = useState('');
    const [summary, setSummary] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [highestPricePoint, setHighestPricePoint] = useState('');
    const [category, setCategory] = useState('');
    const [photos, setPhotos] = useState([]);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [error, setError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [fee, setFee] = useState(0);
    const [stripePromise, setStripePromise] = useState(null);
    const [user, setUser] = useState(null);

    const auth = getAuth();
    const storage = getStorage();
    const navigate = useNavigate();
    
    const categories = [
        'Real Estate',
        'Vehicles & Heavy Equipment',
        'Jewelry/Art/Antiques',
        'Lifestyle'
    ]

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            setUser(currentUser);
        });

        return () => unsubscribe();
    }, [auth]);

    useEffect(() => {
        const fetchBrackets = async () => {
            const bracketsDoc = await getDoc(doc(db, 'Brackets', 'c0xw9q7Ah6C3UK94YoLC'));
            if (bracketsDoc.exists()) {
                const brackets = bracketsDoc.data().AuctionFees;
                calculateFee(brackets);
            }
        };

        if (highestPricePoint) fetchBrackets();
    }, [highestPricePoint]);

    useEffect(() => {
        fetch("https://fqzh94i59u.us-east-2.awsapprunner.com/config").then(async (r) => {
            const { publishableKey } = await r.json();
            setStripePromise(loadStripe(publishableKey));
        });
    }, []);

    const calculateFee = (brackets) => {
        const price = parseFloat(highestPricePoint.replace(/[$,]/g, ''));
        if (isNaN(price)) return;

        let feeCalculated = false;

        for (const key in brackets) {
            const { Num1, Num2, Percentage } = brackets[key];
            if (price >= Num1 && price <= Num2) {
                setFee((price * Percentage) / 100);
                feeCalculated = true;
                break;
            }
        }

        if (!feeCalculated) {
            setFee((price * 2) / 100);
        }    
    };

    const handleFileChange = async (e) => {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        const selectedFiles = Array.from(e.target.files).filter(file => allowedTypes.includes(file.type));

        if (selectedFiles.length + photos.length > 5) {
            alert("You can only upload up to 5 images.");
            return;
        }

        if (selectedFiles.length !== e.target.files.length) {
            alert("Only PNG, JPEG, and JPG files are allowed.");
        }

        setPhotos(prevPhotos => [...prevPhotos, ...selectedFiles]);
    };

    const removePhoto = (index) => {
        setPhotos(prevPhotos => prevPhotos.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!user) {
            setError('Please login to create an auction item.');
            return;
        }

        const userDocRef = doc(db, 'Users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            setError('User details not found.');
            return;
        }

        const userData = userDoc.data();
        if (!userData.AddressLine1 || !userData.Postcode) {
            setError('Please update your AddressLine1 and Postcode in your profile before submitting.');
            return;
        }

        if (!validateForm()) {
            return;
        }

        setIsUploading(true);
        try {
            const uploadedPhotoURLs = await Promise.all(
                photos.map(async (file) => {
                    const fileRef = ref(storage, `auctionItems/${user.uid}/${file.name}`);
                    await uploadBytes(fileRef, file);
                    return await getDownloadURL(fileRef);
                })
            );

            const auctionItem = {
                Name: name,
                Summary: summary,
                Description: description,
                Author: user.uid,
                Value: `${highestPricePoint}`,
                Location: location,
                isApproved: false,
                isFeatured: false,
                DealTerms: '',
                Category: category,
                Photos: uploadedPhotoURLs,
                Rounds: {
                    Round1: {
                        Date: '',
                        endDate: '',
                        Minimum: 0,
                        Participants: [],
                    },
                    Round2: {
                        Date: '',
                        endDate: '',
                        Minimum: 0,
                        Participants: [],
                    },
                    Round3: {
                        Date: '',
                        endDate: '',
                        Minimum: 0,
                        Participants: [],
                    },
                    Round4: {
                        Date: '',
                        endDate: '',
                        Minimum: 0,
                        Participants: [],
                    },
                    Round5: {
                        Date: '',
                        endDate: '',
                        Minimum: 0,
                        Participants: [],
                    },
                },
                EnrolledUsers: [],
                createdAt: serverTimestamp()
            };

            // Add the auction item to Firebase
            await addDoc(collection(db, auctionDb), auctionItem);

            alert('Auction item created successfully!');
            await fetch("https://fqzh94i59u.us-east-2.awsapprunner.com/notifications/auction-submitted", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  to: user.email
                }),
            });

            navigate('/home');

            // Reset form fields
            setName('');
            setSummary('');
            setDescription('');
            setHighestPricePoint(0);
            setLocation('');
            setCategory('');
            setPhotos([]);
            setTermsAccepted(false);
            setError('');
        } catch (err) {
            setError('Failed to create auction item. Please try again.');
        }
    };

    const uploadPhotosToFirebase = async (photos) => {
        const uploadedPhotoUrls = await Promise.all(
            photos.map(async (photo) => {
                const storageRef = ref(storage, `auctionItems/${photo.name}`);
                await uploadBytes(storageRef, photo);
                const url = await getDownloadURL(storageRef);
                return url; // Return the URL
            })
        );
        return uploadedPhotoUrls;
    };

    const validateForm = () => {
        if (!name || !summary || !description || !location || !highestPricePoint || !category || !photos.length || !termsAccepted) {
            setError('Please fill in all fields and accept the terms and conditions.');
            return false;
        }
        return true;
    };
    
    const handlePayment = async () => {
        if (!stripePromise) {
            setError("Stripe is not initialized.");
            return;
        }

        const userDocRef = doc(db, 'Users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            setError('User details not found.');
            return;
        }

        const userData = userDoc.data();
        if (!userData.AddressLine1 || !userData.Postcode) {
            setError('Please update your Address Line 1 and Postcode in your profile before submitting.');
            return;
        }

        if (!validateForm()) {
            return;
        }
    
        const stripe = await stripePromise;
        try {
            // Step 1: Upload photos to Firebase temporary location
            const uploadedPhotos = await uploadPhotosToFirebase(photos);
    
            // Step 2: Send data to backend
            const response = await fetch("https://fqzh94i59u.us-east-2.awsapprunner.com/fee-checkout-session", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    amount: fee,
                    userId: user.uid,
                    auctionItemDetails: { name, summary, description, location, category },
                    photos: uploadedPhotos,
                    value: highestPricePoint
                }),
            });
    
            const { id } = await response.json();
            await stripe.redirectToCheckout({ sessionId: id });
        } catch (err) {
            setError("Payment failed. Please try again.");
        }
    };   
    
    const handlePriceChange = (e) => {
        let value = e.target.value.replace(/[^0-9.]/g, ''); // Remove non-numeric characters except decimal
        if (value) {
            const [integer, decimal] = value.split('.');
            const rawValue = decimal ? `${integer}.${decimal}` : integer; // Store raw value
    
            setHighestPricePoint(rawValue); // Store raw number without formatting
        } else {
            setHighestPricePoint('');
        }
    };
    
    const formatPrice = (value) => {
        if (!value) return '';
        return `$${Number(value).toLocaleString()}`;
    };

    return (
        <div className="createFormContainer">
            <div className="questions">
                <label>Name</label>
                <input
                    type="text"
                    placeholder="Name of the item"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                />
                <label>Summary</label>
                <input
                    type="text"
                    placeholder="Summary"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    required
                />
                <label>Description</label>
                <textarea
                    placeholder="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                ></textarea>
                <label>Location</label>
                <input
                    type="text"
                    placeholder="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                />
                <label>Highest Price Point</label>
                <input
                    type="text"
                    placeholder="Highest Price Point"
                    value={formatPrice(highestPricePoint)}
                    onChange={handlePriceChange}
                    required
                />
                <label>Category</label>
                <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    required
                >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                        <option key={cat} value={cat}>
                            {cat}
                        </option>
                    ))}
                </select>
                <label className="termsCheckBox">
                    <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        required
                    /> I accept the <Link to="/seller-terms-conditions" className="termsLink">Terms & Conditions</Link> as a seller.
                </label>

                {error && <p style={{ color: 'red' }}>{error}</p>}

                {/* <p>${fee.toFixed(2)}</p> */}
                <button type="submit" onClick={handleSubmit}>Submit</button>
                {/* <p style={{ textAlign: 'center', color: '#7C7C7C' }}>To submit auction items a small fee must be collected to generate marketing materials</p> */}
            </div>
            <div className="filesContainer">
                <label>Photos (Max 5)</label>
                <div className="files">
                    <input 
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        required
                    />

                    <div className="uploaded-images">
                        {photos.map((photo, index) => (
                            <div key={index} className="image-preview">
                                <img src={URL.createObjectURL(photo)} alt={`Uploaded ${index}`} />
                                <button type="button" onClick={() => removePhoto(index)}>Remove</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            
        </div>
    )
}

export default CreateAuctionItem;